import React, { useState, useEffect } from 'react';
import { View, StyleSheet, Modal, Text, TouchableOpacity, ActivityIndicator, Platform, useWindowDimensions } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import DropDownPicker from 'react-native-dropdown-picker';
import { DLODropdownOption } from '../types/dlo.types';
import { useTheme } from '../../../theme';

interface LocationModalProps {
  visible: boolean;
  onClose: () => void;
  selectedLocation?: DLODropdownOption | null;
  locationOptions: DLODropdownOption[];
  onSave?: (selectedLocation: DLODropdownOption | null) => void;
  isLoading?: boolean;
  title?: string;
  titleAlign?: 'left' | 'center';
}

const LocationModal: React.FC<LocationModalProps> = ({
  visible,
  onClose,
  selectedLocation,
  locationOptions,
  onSave,
  isLoading = false,
  title,
  titleAlign,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';
  const [open, setOpen] = useState<boolean>(false);
  const [value, setValue] = useState<string | null>(selectedLocation ? selectedLocation.value : null);
  const [items, setItems] = useState<Array<{ label: string; value: string; disabled?: boolean }>>(
    locationOptions.map((o, index) => ({ 
      label: o.label, 
      value: o.value || `location-${index}`, // Ensure unique value
      disabled: o.disabled 
    }))
  );
  
  const { width: screenWidth } = useWindowDimensions();
  
  // Calculate modal width: 419px for web/desktop, 80% for mobile
  const modalWidth = Platform.OS === 'web' ? 419 : screenWidth * 0.8;

  useEffect(() => {
    setValue(selectedLocation ? selectedLocation.value : null);
  }, [selectedLocation]);

  useEffect(() => {
    // Filter out duplicates and ensure unique keys
    const uniqueItems = locationOptions.reduce((acc, option, index) => {
      const exists = acc.find(item => item.value === option.value);
      if (!exists && option.value) {
        acc.push({ 
          label: option.label, 
          value: option.value, 
          disabled: option.disabled 
        });
      }
      return acc;
    }, [] as Array<{ label: string; value: string; disabled?: boolean }>);
    
    setItems(uniqueItems);
  }, [locationOptions]);

  const handleSave = () => {
    if (onSave) {
      const selected = locationOptions.find((o) => o.value === value) || null;
      onSave(selected);
    }
  };

  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="none"
      onRequestClose={handleClose}
      supportedOrientations={['portrait', 'landscape', 'landscape-left', 'landscape-right']}
    >
      <TouchableOpacity 
        style={styles.modalOverlay} 
        activeOpacity={1}
        onPress={handleClose}
      >
        <TouchableOpacity
          style={[styles.modalContent, { width: modalWidth }]}
          activeOpacity={1}
          onPress={(e) => e.stopPropagation()}
        >
          {/* Body + Footer Container */}
          <View style={[
            styles.bodyFooterContainer,
            isDark && {
              backgroundColor: '#202020',
              borderColor: '#3A3F4A',
              shadowColor: 'rgba(0,0,0,0.6)',
            },
          ]}>
            {/* Body */}
            <View style={styles.modalBody}>
              {/* Title */}
              <View style={styles.titleContainer}>
                <Text style={[styles.modalTitle, isDark && { color: colors.text.primary }]}>
                  {title ?? "Select Location"}
                </Text>
              </View>

              {/* Dropdown */}
              <View style={styles.dropdownContainer}>
                {/* Form Label */}
                <Text style={[styles.formLabel, isDark && { color: colors.text.secondary }]}>Location</Text>

                {/* Picker Wrapper */}
                <View style={styles.pickerWrapper}>
                  <DropDownPicker
                    open={open}
                    value={value}
                    items={items}
                    setOpen={setOpen}
                    setValue={setValue}
                    setItems={setItems}
                    placeholder="Select a location"
                    listMode="SCROLLVIEW"
                    searchable={true}
                    searchPlaceholder="Search locations..."
                    style={[
                      styles.dropdown,
                      isDark && {
                        backgroundColor: colors.input.background,
                        borderColor: colors.input.border,
                      },
                    ]}
                    dropDownContainerStyle={[
                      styles.dropdownList,
                      isDark && {
                        backgroundColor: colors.background.elevated,
                        borderColor: colors.input.border,
                      },
                    ]}
                    textStyle={[
                      styles.dropdownText,
                      isDark && { color: colors.text.primary },
                    ]}
                    placeholderStyle={[
                      styles.dropdownPlaceholder,
                      isDark && { color: colors.text.disabled },
                    ]}
                    searchContainerStyle={[
                      styles.searchContainerStyle,
                      isDark && { borderBottomColor: colors.border.default },
                    ]}
                    searchTextInputStyle={[
                      styles.searchsearchTextInputStyle,
                      isDark && {
                        backgroundColor: colors.input.background,
                        borderColor: colors.input.border,
                        color: colors.text.primary,
                      },
                    ]}
                    arrowIconStyle={{ tintColor: isDark ? colors.text.secondary : '#3A3A3A' } as any}
                    tickIconStyle={{ tintColor: '#FFFFFF' } as any}
                    selectedItemContainerStyle={{ backgroundColor: colors.brand.primary, borderTopWidth: 0 }}
                    selectedItemLabelStyle={{ color: '#FFFFFF', fontWeight: '700' }}
                    listItemContainerStyle={isDark ? { backgroundColor: colors.background.elevated } : undefined}
                    listItemLabelStyle={isDark ? { color: colors.text.primary } : undefined}
                  />
                </View>
              </View>
            </View>

            {/* Button Hierarchy (Footer) */}
            <View style={styles.buttonHierarchy}>
              {/* Tertiary Cancel Button */}
              <TouchableOpacity
                style={[
                  styles.tertiaryButton,
                  isDark && {
                    borderWidth: 1,
                    borderColor: colors.brand.primary,
                    borderRadius: 24,
                  },
                ]}
                onPress={handleClose}
              >
                <View style={styles.buttonContent}>
                  <Text style={[styles.tertiaryButtonText, isDark && { color: colors.brand.primary }]}>Cancel</Text>
                </View>
              </TouchableOpacity>

              {/* Primary Save Button */}
              <TouchableOpacity
                style={[
                  styles.primaryButton,
                  (!value || isLoading) && styles.primaryButtonDisabled
                ]}
                onPress={handleSave}
                disabled={!value || isLoading}
              >
                <View style={styles.buttonContent}>
                  {isLoading ? (
                    <ActivityIndicator size="small" color="#FFFFFF" />
                  ) : (
                    <Text style={styles.primaryButtonText}>Save</Text>
                  )}
                </View>
              </TouchableOpacity>
            </View>
          </View>

          {/* Close Button Icon (Top Right) */}
          <TouchableOpacity
            style={styles.closeButton}
            onPress={handleClose}
          >
            <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <Path
                d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
                fill={isDark ? colors.text.primary : '#3A3A3A'}
              />
            </Svg>
          </TouchableOpacity>
        </TouchableOpacity>
      </TouchableOpacity>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: 'transparent',
    maxWidth: 419,
    height: 226,
    position: 'relative',
  },
  bodyFooterContainer: {
    backgroundColor: '#FBFCFE',
    borderRadius: 8,
    borderWidth: 10,
    borderColor: '#D7E5F8',
    padding: 32,
    gap: 16,
    shadowColor: 'rgba(0, 92, 188, 0.12)',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 1,
    shadowRadius: 24,
    elevation: 8,
  },
  modalBody: {
    gap: 16,
    paddingBottom: 8,
  },
  titleContainer: {
    paddingRight: 24,
  },
  modalTitle: {
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
    }),
    fontWeight: '700',
    fontSize: 24,
    lineHeight: 32,
    color: '#3A3A3A',
    textAlign: 'left',
  },
  dropdownContainer: {
    gap: 8,
  },
  formLabel: {
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
    }),
    fontWeight: '400',
    fontSize: 12,
    lineHeight: 18,
    color: '#3A3A3A',
  },
  pickerWrapper: {
    zIndex: 9999,
    position: 'relative',
  },
  dropdown: {
    backgroundColor: '#FBFCFE',
    borderWidth: 1,
    borderColor: '#C0C0C0',
    borderRadius: 4,
    minHeight: 32,
  },
  dropdownList: {
    borderWidth: 1,
    borderColor: '#C0C0C0',
    borderRadius: 4,
  },
  dropdownText: {
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
    }),
    fontWeight: '400',
    fontSize: 14,
    lineHeight: 20,
    color: '#3A3A3A',
  },
  dropdownPlaceholder: {
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
    }),
    fontWeight: '400',
    fontSize: 14,
    lineHeight: 20,
    color: '#9CA3AF',
  },
  searchsearchTextInputStyle: {
    backgroundColor: '#FBFCFE',
    borderWidth: 1,
    borderColor: '#C0C0C0',
    borderRadius: 4,
    zIndex: 10000,
  },
  searchContainerStyle: {
    borderColor: 'transparent',
    borderBottomWidth: 0,
  },
  buttonHierarchy: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 16,
    zIndex: -99999
  },
  buttonContent: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 4,
    paddingHorizontal: 16,
  },
  tertiaryButton: {
    height: 32,
    justifyContent: 'center',
  },
  tertiaryButtonText: {
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
    }),
    fontWeight: '700',
    fontSize: 14,
    lineHeight: 20,
    color: '#005CBC',
  },
  primaryButton: {
    height: 32,
    backgroundColor: '#005CBC',
    borderRadius: 24,
    justifyContent: 'center',
  },
  primaryButtonDisabled: {
    backgroundColor: '#9CA3AF',
    opacity: 0.6,
  },
  primaryButtonText: {
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
    }),
    fontWeight: '700',
    fontSize: 14,
    lineHeight: 20,
    color: '#FFFFFF',
  },
  closeButton: {
    position: 'absolute',
    top: 32,
    right: 32,
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default LocationModal;
